/**
 * Downloads a Blob or File object as a file with the specified filename.
 * 
 * @param {Blob} blob - The Blob or File object to download
 * @param {string} fileName - The name to save the file as
 */
export const saveBlob = (blob, fileName) => {
    if (!(blob instanceof Blob)) {
        throw new Error('First argument must be a Blob or File object');
    }
    if (!fileName || typeof fileName !== 'string') {
        throw new Error('fileName must be a non-empty string');
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';

    try {
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (error) {
        a.remove();
        URL.revokeObjectURL(url);
        throw new Error(`Failed to initiate download: ${error.message}`);
    }
};

/**
 * Opens a Blob or File object in a new window/tab with optional download behavior.
 * 
 * @param {Blob} blob - The Blob or File object to open
 * @param {boolean} useNewWindow - Whether to open in a new window with toolbar (true) or tab (false)
 * @param {boolean} enableDownload - Whether to trigger a download in addition to displaying in browser (default false)
 * @param {string|null} fileName - Optional filename for download (required when enableDownload is true)
 * @param {number} timeoutMs - Timeout in milliseconds for cleanup fallback (default 60000)
 */
export const openBlob = (blob, useNewWindow = false, enableDownload = false, fileName = null, timeoutMs = 60000) => {
    if (!(blob instanceof Blob)) {
        throw new Error('First argument must be a Blob or File object');
    }
    if (typeof timeoutMs !== 'number' || timeoutMs <= 0) {
        throw new Error('timeoutMs must be a positive number');
    }
    if (enableDownload && (fileName === null || typeof fileName !== 'string')) {
        throw new Error('fileName must be provided as a string when enableDownload is true');
    }
    
    // Configure window features for popup if useNewWindow is true
    const windowFeatures = useNewWindow ? 
        'toolbar=yes,location=no,status=yes,menubar=yes,scrollbars=yes,resizable=yes' : 
        '';
    // Create a temporary URL that points to the blob content
    const url = URL.createObjectURL(blob);
    let isCleanedUp = false;
    let a = null;
    let cleanupTimeout = null;
    let newWindow = null;
    
    // Cleanup function to prevent memory leaks by revoking the blob URL and removing elements
    const cleanup = () => {
        if (!isCleanedUp) {
            isCleanedUp = true;
            URL.revokeObjectURL(url);
            if (a) {
                a.remove();
                a = null;
            }
            if (cleanupTimeout) {
                clearTimeout(cleanupTimeout);
                cleanupTimeout = null;
            }
            window.removeEventListener('unload', cleanup);
            newWindow = null;
        }
    };

    try {
        // If enableDownload is true, trigger the download first
        if (enableDownload) {
            a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            a.remove();
            a = null;
        }
        
        // Open the blob URL in a new window/tab
        newWindow = window.open(url, '_blank', windowFeatures);
        
        // Handle popup blockers
        if (!newWindow) {
            cleanup();
            throw new Error('Popup was blocked or failed to open. Please enable popups for this site.');
        }
        
        // Ensure cleanup happens when the parent window is closed/refreshed
        window.addEventListener('unload', cleanup);
        
        try {
            // Attempt to detect when the new window has finished loading
            // This helps ensure timely cleanup of resources
            newWindow.document.addEventListener('readystatechange', () => {
                if (newWindow.document.readyState === 'complete') {
                    cleanup();
                }
            }, { once: true });
        } catch (e) {
            // If we can't access the document due to cross-origin restrictions,
            // we'll rely on the unload event and timeout for cleanup
            console.warn('Could not access new window document:', e);
        }
        
        // Ensure cleanup happens when the new window is closed
        newWindow.addEventListener('unload', cleanup, { once: true });
        
        // Handle any errors that occur in the new window
        newWindow.onerror = () => {
            cleanup();
            return false;
        };
        
        // Fallback cleanup in case other cleanup triggers fail
        // This prevents memory leaks if the window stays open indefinitely
        cleanupTimeout = setTimeout(cleanup, timeoutMs);
        
    } catch (error) {
        cleanup();
        throw error;
    } finally {
        // Extra safety check to remove the anchor element if it exists and is still in the DOM
        if (a && a.parentNode) {
            a.remove();
            a = null;
        }
    }
};